"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, LazyMotion, domAnimation, m } from "motion/react";
import Image from "@/components/OptimizedImage";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useCookieConsent } from "@lightwaves/hooks";
import { getExifData } from "@/lib/exif-loader";

interface PrintData {
  image: string;
  name?: string;
  shoplink?: string;
  price?: string | number;
  currency?: string;
  sku?: string;
  variations?: string[];
  print?: string;
  paper?: string;
  edition?: string;
  weight?: number;
  additional_lightbox_images?: { link: string; alt: string }[];
}

interface LightboxProps {
  children: React.ReactNode;
  shopLink?: string | null;
  prints?: PrintData[];
  ecommerceEnabled?: boolean;
  siteUrl?: string;
  showExifData?: boolean;
}

export function LightboxProvider({ children, shopLink, prints, ecommerceEnabled = false, siteUrl = '', showExifData = false }: LightboxProps) {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [activeLightboxUrl, setActiveLightboxUrl] = useState<string>("");
  const [allImages, setAllImages] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { hasConsent } = useCookieConsent();
  const scrollPositionRef = useRef<number>(0);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Snipcart mode: ecommerce enabled AND user has given consent
  const snipcartMode = ecommerceEnabled && hasConsent;

  const hasValidPrice = (price: string | number | undefined): boolean => {
    if (price === undefined || price === null) return false;
    const priceStr = String(price).trim();
    if (!priceStr || priceStr.toUpperCase() === "TBD") return false;
    return !isNaN(parseFloat(priceStr));
  };

  // Find the current print based on the lightbox image
  const getCurrentPrint = (): PrintData | null => {
    if (!lightboxImage || !prints) return null;
    // Decode the lightbox image URL to handle Next.js Image encoding
    const decodedLightboxImage = decodeURIComponent(lightboxImage);
    return prints.find(p => {
      // Check if the print image path is contained in the lightbox image URL
      // This handles both direct paths and Next.js optimized image URLs
      return decodedLightboxImage.includes(p.image) ||
             lightboxImage.includes(p.image) ||
             p.image.includes(lightboxImage) ||
             // Also check just the filename for extra robustness
             decodedLightboxImage.includes(p.image.split('/').pop() || '');
    }) || null;
  };

  // Determine if Buy Print button should be shown
  const shouldShowBuyButton = (): boolean => {
    if (snipcartMode) {
      // In Snipcart mode, price is the trigger
      const currentPrint = getCurrentPrint();
      return currentPrint ? hasValidPrice(currentPrint.price) : false;
    } else {
      // Not in Snipcart mode, shoplink is the trigger
      if (shopLink) return true;
      const currentPrint = getCurrentPrint();
      return currentPrint?.shoplink ? true : false;
    }
  };

  // Get the shop link for the current image
  const getCurrentShopLink = (): string | null => {
    if (shopLink) return shopLink;
    const currentPrint = getCurrentPrint();
    return currentPrint?.shoplink || null;
  };

  // Scroll lock is applied synchronously in handleContentClick via CSS class
  // Cleanup is handled by onExitComplete to wait for exit animation

  // Remove scroll lock after exit animation completes
  const handleExitComplete = useCallback(() => {
    if (!isLightboxOpen) {
      const scrollY = scrollPositionRef.current;
      document.documentElement.classList.remove('has-lightbox-open');
      // Restore scroll position after removing scroll lock
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
    }
  }, [isLightboxOpen]);

  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a');
    const isImageLink = link && link.href && (link.href.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i));
    const isDirectImage = target.tagName === 'IMG' && !link;

    if (isImageLink || isDirectImage) {
      e.preventDefault();
      e.stopPropagation();

      // Apply scroll lock using CSS class (defined in globals.css)
      const scrollY = window.scrollY;
      scrollPositionRef.current = scrollY;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      document.documentElement.classList.add('has-lightbox-open');
      // Restore scroll position immediately
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });

      const container = e.currentTarget;
      const imgElements = Array.from(container.querySelectorAll('img'));
      // Sort by data-lightbox-index if present, otherwise keep DOM order
      const hasLightboxIndex = imgElements.some(img => img.hasAttribute('data-lightbox-index'));
      if (hasLightboxIndex) {
        imgElements.sort((a, b) => {
          const aIdx = parseInt(a.getAttribute('data-lightbox-index') || '999999', 10);
          const bIdx = parseInt(b.getAttribute('data-lightbox-index') || '999999', 10);
          return aIdx - bIdx;
        });
      }
      const images = imgElements.map(img => img.getAttribute('src') || "").filter(src => src !== "");
      const currentSrc = target.getAttribute('src') || (link ? link.href : "");
      setAllImages(images);
      setCurrentIndex(images.indexOf(currentSrc));
      setLightboxImage(currentSrc);
      setIsLightboxOpen(true);
    }
  };

  const navigateLightbox = useCallback((direction: 'next' | 'prev') => {
    if (allImages.length === 0) return;
    let newIndex = direction === 'next'
      ? (currentIndex + 1) % allImages.length
      : (currentIndex - 1 + allImages.length) % allImages.length;
    setCurrentIndex(newIndex);
    setLightboxImage(allImages[newIndex]);
    // Reset active variant when navigating to a new image
    setActiveLightboxUrl("");
  }, [currentIndex, allImages]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
    touchStartRef.current = null;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      dx < 0 ? navigateLightbox('next') : navigateLightbox('prev');
    }
  }, [navigateLightbox]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (lightboxImage) {
        if (event.key === 'Escape') { setIsLightboxOpen(false); setLightboxImage(null); }
        else if (event.key === 'ArrowRight') navigateLightbox('next');
        else if (event.key === 'ArrowLeft') navigateLightbox('prev');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, navigateLightbox]);

  return (
    <LazyMotion features={domAnimation}>
      <div onClickCapture={handleContentClick} className="w-full dark:prose-invert mx-auto max-w-none [&_img]:cursor-pointer">
        {children}
      </div>

      {/* Backdrop - fades out slower to hide any scroll hiccup */}
      <AnimatePresence>
        {lightboxImage && (
          <m.div
            key="lightbox-backdrop"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 z-[69] bg-white"
          />
        )}
      </AnimatePresence>

      {/* Content - fades out faster */}
      <AnimatePresence onExitComplete={handleExitComplete}>
        {lightboxImage && (
          <m.div
            key="lightbox-content"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="fixed inset-0 z-[70] flex h-[100dvh] w-screen flex-col overflow-hidden"
            onClick={() => { setIsLightboxOpen(false); setLightboxImage(null); }}
          >
            <div className="flex h-12 shrink-0 items-center justify-end px-4 md:px-8">
              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setIsLightboxOpen(false); setLightboxImage(null); }} className="rounded-full bg-gray-100">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 md:px-12" onClick={(e) => e.stopPropagation()} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
              {allImages.length > 1 && (
                <>
                  <Button variant="ghost" size="icon" onClick={() => navigateLightbox('prev')} className="absolute left-2 top-1/2 z-50 hidden -translate-y-1/2 rounded-full bg-gray-50/80 md:flex">
                    <ChevronLeft className="h-10 w-10" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => navigateLightbox('next')} className="absolute right-2 top-1/2 z-50 hidden -translate-y-1/2 rounded-full bg-gray-50/80 md:flex">
                    <ChevronRight className="h-10 w-10" />
                  </Button>
                </>
              )}
              <div className="flex h-full w-full items-center justify-center overflow-hidden relative">
                <Image src={activeLightboxUrl || lightboxImage} alt="Lightbox view" fill sizes="100vw" className="object-contain pointer-events-none" />
              </div>

              {/* Thumbnail navigation for additional images */}
              {(() => {
                const currentPrint = getCurrentPrint();
                if (!currentPrint?.additional_lightbox_images?.length) return null;
                const allVariants = [{ link: currentPrint.image, alt: 'Main' }, ...currentPrint.additional_lightbox_images];
                return (
                  <div className="absolute -right-16 top-0 hidden xl:flex flex-col gap-2">
                    {allVariants.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveLightboxUrl(img.link)}
                        aria-label={`View ${img.alt || `image ${idx + 1}`}`}
                        aria-pressed={(activeLightboxUrl || lightboxImage) === img.link}
                        className={`relative size-12 rounded border overflow-hidden transition-opacity ${(activeLightboxUrl || lightboxImage) === img.link ? "border-black" : "border-transparent opacity-50 hover:opacity-75"}`}
                      >
                        <Image src={img.link} alt={img.alt || ''} fill sizes="48px" className="object-cover" />
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div className="flex shrink-0 flex-col items-center gap-3 py-4 md:py-6 bg-white" onClick={(e) => e.stopPropagation()}>
              {/* Title */}
              {(() => {
                const currentPrint = getCurrentPrint();
                if (!currentPrint?.name) return null;
                return (
                  <div className="flex flex-col gap-1 text-center w-full">
                    <h3 className="text-xl font-medium uppercase">{currentPrint.name}</h3>
                  </div>
                );
              })()}

              {shouldShowBuyButton() && (() => {
                const currentPrint = getCurrentPrint();

                // Snipcart mode: render add-to-cart button with all data attributes
                if (snipcartMode && currentPrint && hasValidPrice(currentPrint.price) && currentPrint.sku) {
                  const variationString = currentPrint.variations ? currentPrint.variations.join('|') : "";
                  return (
                    <button
                      type="button"
                      className="snipcart-add-item px-10 py-4 text-lg font-medium shadow-xl rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                      data-item-id={currentPrint.sku}
                      data-item-price={currentPrint.price}
                      data-item-url="/"
                      data-item-name={currentPrint.name || 'Print'}
                      data-item-image={`${siteUrl}${currentPrint.image}`}
                      data-item-currency={currentPrint.currency || 'USD'}
                      data-item-shippable="true"
                      data-item-weight={currentPrint.weight || 500}
                      {...(variationString && { 'data-item-custom1-name': 'Size & Edition', 'data-item-custom1-options': variationString })}
                      {...(currentPrint.print && { 'data-item-custom2-name': 'Print', 'data-item-custom2-value': currentPrint.print, 'data-item-custom2-type': 'readonly' })}
                      {...(currentPrint.paper && { 'data-item-custom3-name': 'Paper', 'data-item-custom3-value': currentPrint.paper, 'data-item-custom3-type': 'readonly' })}
                      {...(currentPrint.edition && { 'data-item-custom4-name': 'Edition', 'data-item-custom4-value': currentPrint.edition, 'data-item-custom4-type': 'readonly' })}
                    >
                      Buy Print
                    </button>
                  );
                }

                // Fallback: external shop link
                const link = getCurrentShopLink();
                if (link) {
                  return (
                    <Button
                      size="lg"
                      className="px-10 py-6 text-lg font-medium shadow-xl rounded-lg"
                      onClick={() => window.open(link, '_blank')}
                    >
                      Buy Print
                    </Button>
                  );
                }

                return null;
              })()}

              {/* EXIF Data Display */}
              {(() => {
                if (!showExifData || !lightboxImage) return null;
                const exif = getExifData(activeLightboxUrl || lightboxImage);
                if (!exif) return null;
                return (
                  <div className="flex flex-wrap gap-3 justify-center text-xs text-gray-500">
                    {exif.camera && (
                      <span className="flex items-center gap-1" title="Camera">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        {exif.camera}
                      </span>
                    )}
                    {exif.lens && (
                      <span className="flex items-center gap-1" title="Lens">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth={2} /><circle cx="12" cy="12" r="6" strokeWidth={2} /><circle cx="12" cy="12" r="2" strokeWidth={2} /></svg>
                        {exif.lens}
                      </span>
                    )}
                    {exif.focalLength && (
                      <span className="flex items-center gap-1 font-mono" title="Focal Length">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m-3-3h6" /></svg>
                        {exif.focalLength}
                      </span>
                    )}
                    {exif.aperture && (
                      <span className="flex items-center gap-1 font-mono" title="Aperture">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><polygon strokeWidth={2} points="12,2 15,9 22,9 17,14 19,22 12,17 5,22 7,14 2,9 9,9" /></svg>
                        ƒ/{exif.aperture}
                      </span>
                    )}
                    {exif.shutterSpeed && (
                      <span className="flex items-center gap-1 font-mono" title="Shutter Speed">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth={2} /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" /></svg>
                        {exif.shutterSpeed}s
                      </span>
                    )}
                    {exif.iso && (
                      <span className="flex items-center gap-1 font-mono" title="ISO">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                        ISO {exif.iso}
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Mobile thumbnail navigation for additional images */}
              {(() => {
                const currentPrint = getCurrentPrint();
                if (!currentPrint?.additional_lightbox_images?.length) return null;
                const allVariants = [{ link: currentPrint.image, alt: 'Main' }, ...currentPrint.additional_lightbox_images];
                return (
                  <div className="flex xl:hidden gap-2 justify-center">
                    {allVariants.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveLightboxUrl(img.link)}
                        aria-label={`View ${img.alt || `image ${idx + 1}`}`}
                        aria-pressed={(activeLightboxUrl || lightboxImage) === img.link}
                        className={`relative size-8 rounded border overflow-hidden transition-opacity ${(activeLightboxUrl || lightboxImage) === img.link ? "border-black" : "border-transparent opacity-50 hover:opacity-75"}`}
                      >
                        <Image src={img.link} alt={img.alt || ''} fill sizes="48px" className="object-cover" />
                      </button>
                    ))}
                  </div>
                );
              })()}

              {allImages.length > 1 && (
                <div className="flex items-center gap-8 md:hidden">
                  <Button variant="outline" size="icon" onClick={() => navigateLightbox('prev')} className="h-12 w-12 rounded-full"><ChevronLeft /></Button>
                  <span className="text-sm tabular-nums">{currentIndex + 1} / {allImages.length}</span>
                  <Button variant="outline" size="icon" onClick={() => navigateLightbox('next')} className="h-12 w-12 rounded-full"><ChevronRight /></Button>
                </div>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
}

interface ProjectMetadata {
  projectDate?: string;
  location?: string;
  phase?: string;
  camera?: string;
  director?: string;
  production?: string;
  title?: string;
  description?: string;
  techniques?: string[];
  tags?: string[];
  printInfo?: string;
}

export function ProjectInfoTable({ metadata }: { metadata: ProjectMetadata }) {
  // Helper to parse [text](url) and \n from gray-matter strings without trailing newlines
  const renderFormattedText = (text: string) => {
    if (!text) return null;
    // Use .trim() to remove trailing newlines, but preserve internal ones
    return text.trim().split("\n").map((line, i) => {
      const parts = line.split(/(\[.*?\]\(.*?\))/g);

      return (
        <p key={i} className="leading-snug">
          {parts.map((part, index) => {
            const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
            if (linkMatch) {
              return (
                <a
                  key={index}
                  href={linkMatch[2]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-4 hover:opacity-70 transition-opacity"
                >
                  {linkMatch[1]}
                </a>
              );
            }
            return part;
          })}
        </p>
      );
    });
  };

  const rows = [
    { label: "Project Date", value: metadata.projectDate },
    { label: "Location", value: metadata.location },
    { label: "Phase", value: metadata.phase },
    { label: "Camera", value: metadata.camera },
    { label: "Director", value: metadata.director },
    { label: "Production", value: metadata.production },
    { label: "Print Info", value: metadata.printInfo },
    {
      label: "Techniques",
      value: metadata.techniques && metadata.techniques.length > 0 ? metadata.techniques : null
    },
  ];

  const visibleRows = rows.filter((row) => {
    if (Array.isArray(row.value)) return row.value.length > 0;
    return row.value != null && row.value !== "";
  });

  if (visibleRows.length === 0) return null;

  return (
    <div className="my-8 rounded-lg border bg-card">
      <Table>
        <TableBody>
          {visibleRows.map((row) => (
            <TableRow key={row.label}>
              <TableCell className="w-0 whitespace-nowrap align-top font-medium text-muted-foreground py-2 pr-4">
                {row.label}
              </TableCell>
              <TableCell className="py-2 w-full">
                {Array.isArray(row.value) ? (
                  <div className="flex flex-wrap gap-1.5">
                    {row.value.map((item) => (
                      <Badge key={item} variant="secondary">
                        {item}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {renderFormattedText(row.value as string)}
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
